import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import RadioGroup from '@material-ui/core/RadioGroup'
import Radio from '@material-ui/core/Radio'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import TextField from '@material-ui/core/TextField'
import { sendAddOnRenewal, updateAddOnEmail } from '../../store/actions/membershipActions'
import { RENEW_FIRST_MESSAGE } from '../shared/addOnRenewal'

export const renewQuestion = (name) => `Add ${name}'s plan to my subscription?`

/**
 * The Renew / Resend button on one accepted add-on row, for the primary
 * (add-on renewal spec, section 3, flow A).
 *
 * Clicking asks whether the primary will carry the add-on's plan. Yes or No,
 * the renewal email is sent; Cancel sends nothing. Nothing is written to
 * Stripe here - that happens only after the add-on signs.
 *
 *   action          'renew' | 'resend'
 *   primaryRenewed  false disables the button with "Renew your membership first"
 *   paysForAddOns   the primary's family-wide answer; pre-selects Yes
 *   primaryName     the primary's name, for "{primary} pays"
 *   onSent          called after a successful send (refresh the family)
 *
 * Once the email has gone ('resend'), the row offers Resend (keeps who pays),
 * Change who pays (re-sends the matching email) and, for an add-on with no
 * online account, Edit email (re-sends to the new address).
 */
const AddOnRenewAction = (props) => props.action === 'resend'
    ? <AfterEmailActions {...props} />
    : <RenewFirstAction {...props} />

const PRE_SIGNING = ['emailed', 'in_progress']

const AfterEmailActions = ({ member, primaryRenewed, primaryName, onSent }) => {
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [busy, setBusy] = useState(false)
    const [payerOpen, setPayerOpen] = useState(false)
    const [emailOpen, setEmailOpen] = useState(false)
    const renewal = member.renewal || {}
    const primaryPays = renewal.primaryPays === true
    const [payer, setPayer] = useState(primaryPays ? 'primary' : 'self')
    const [email, setEmail] = useState(member.email || '')
    const [emailError, setEmailError] = useState(null)
    const name = member.name || member.email
    const who = primaryName || 'I'
    // The backend refuses any send until the primary has renewed.
    const canChangePayer = primaryRenewed && PRE_SIGNING.indexOf(renewal.status) !== -1
    const canEditEmail = canChangePayer && !(member.member && member.member.allcalUserId)

    const report = (err, success) => {
        setBusy(false)
        if (!err) {
            enqueueSnackbar(success, { variant: 'success' })
            if (onSent) onSent()
            return
        }
        if (err.status === 409 && err.message === RENEW_FIRST_MESSAGE) {
            enqueueSnackbar(RENEW_FIRST_MESSAGE, { variant: 'warning' })
            return
        }
        enqueueSnackbar(err.message || 'Something went wrong, please try again', { variant: 'error' })
    }

    const resend = () => {
        setBusy(true)
        dispatch(sendAddOnRenewal(member.addOnId, undefined, (err) => report(err, `Renewal email sent to ${member.email || name}`)))
    }

    const savePayer = () => {
        setPayerOpen(false)
        setBusy(true)
        const pays = payer === 'primary'
        dispatch(sendAddOnRenewal(member.addOnId, pays, (err) => report(err,
            `${pays ? (primaryName ? `${primaryName} pays` : 'You pay') : `${name} pays`} for this plan. An updated renewal email has been sent to ${member.email || name}.`)))
    }

    const saveEmail = () => {
        const next = email.trim()
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(next)) {
            setEmailError('Enter a valid email address')
            return
        }
        setEmailOpen(false)
        setBusy(true)
        dispatch(updateAddOnEmail(member.addOnId, next, (err, data) => report(err,
            data && data.resent ? `Email updated. The renewal email has been sent to ${data.email}.` : 'Email updated.')))
    }

    return <>
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button color='primary' size='small' variant='outlined' disabled={!primaryRenewed || busy} onClick={resend}
                startIcon={busy ? <CircularProgress size={14} /> : null}>Resend</Button>
            {canChangePayer && <Button size='small' variant='outlined' disabled={busy} onClick={() => { setPayer(primaryPays ? 'primary' : 'self'); setPayerOpen(true) }}>Change who pays</Button>}
            {canEditEmail && <Button size='small' disabled={busy} onClick={() => { setEmail(member.email || ''); setEmailError(null); setEmailOpen(true) }}>Edit email</Button>}
        </span>

        <Dialog open={payerOpen} onClose={() => setPayerOpen(false)} aria-labelledby={`payer-${member.addOnId}`}>
            <DialogTitle id={`payer-${member.addOnId}`}>Who pays for {name}'s plan?</DialogTitle>
            <DialogContent>
                <RadioGroup name='payer' value={payer} onChange={(e) => setPayer(e.target.value)}>
                    <FormControlLabel value='primary' control={<Radio color='primary' />} label={`${who} pay${primaryName ? 's' : ''} (added to ${primaryName ? `${primaryName}'s` : 'my'} subscription)`} />
                    <FormControlLabel value='self' control={<Radio color='primary' />} label={`${name} pays for their own plan`} />
                </RadioGroup>
                <Typography variant='body2' color='textSecondary'>
                    {name} gets an updated renewal email straight away.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPayerOpen(false)}>Cancel</Button>
                <Button color='primary' variant='contained' onClick={savePayer}>Save and send email</Button>
            </DialogActions>
        </Dialog>

        <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} aria-labelledby={`email-${member.addOnId}`} fullWidth maxWidth='xs'>
            <DialogTitle id={`email-${member.addOnId}`}>Edit {name}'s email</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    id={`addon-email-${member.addOnId}`}
                    fullWidth
                    variant='outlined'
                    size='small'
                    label='Email'
                    type='email'
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(null) }}
                    error={!!emailError}
                    helperText={emailError || 'The renewal email is sent to this address.'}
                    InputLabelProps={{ style: { padding: 0 } }}
                    style={{ padding: 0, marginTop: 8 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEmailOpen(false)}>Cancel</Button>
                <Button color='primary' variant='contained' onClick={saveEmail}>Save and send email</Button>
            </DialogActions>
        </Dialog>
    </>
}

const RenewFirstAction = ({ member, action, primaryRenewed, paysForAddOns = false, onSent }) => {
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [open, setOpen] = useState(false)
    const [answer, setAnswer] = useState(paysForAddOns ? 'yes' : 'no')
    const [sending, setSending] = useState(false)

    const name = member.name || member.email

    const openDialog = () => {
        setAnswer(paysForAddOns ? 'yes' : 'no')
        setOpen(true)
    }

    const send = () => {
        setOpen(false)
        setSending(true)
        dispatch(sendAddOnRenewal(member.addOnId, answer === 'yes', (err) => {
            setSending(false)
            if (!err) {
                enqueueSnackbar(`Renewal email sent to ${name}`, { variant: 'success' })
                if (onSent) onSent()
                return
            }
            if (err.status === 409 && err.message === RENEW_FIRST_MESSAGE) {
                enqueueSnackbar(RENEW_FIRST_MESSAGE, { variant: 'warning' })
                return
            }
            enqueueSnackbar(err.message || 'The renewal email could not be sent, please try again', { variant: 'error' })
        }))
    }

    return <>
        <Button
            color='primary'
            size='small'
            variant='outlined'
            disabled={!primaryRenewed || sending}
            onClick={openDialog}
            startIcon={sending ? <CircularProgress size={14} /> : null}
        >
            {action === 'resend' ? 'Resend' : 'Renew'}
        </Button>
        {!primaryRenewed && (
            <Typography variant='body2' color='textSecondary'>{RENEW_FIRST_MESSAGE}</Typography>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby={`renew-addon-${member.addOnId}`}>
            <DialogTitle id={`renew-addon-${member.addOnId}`}>{renewQuestion(name)}</DialogTitle>
            <DialogContent>
                <RadioGroup name='authorizePrimary' value={answer} onChange={(e) => setAnswer(e.target.value)}>
                    <FormControlLabel value='yes' control={<Radio color='primary' />} label='Yes' />
                    <FormControlLabel value='no' control={<Radio color='primary' />} label={`No, ${name} pays for their own plan`} />
                </RadioGroup>
                <Typography variant='body2' color='textSecondary'>
                    Either way, {name} gets an email to complete their renewal.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>Cancel</Button>
                <Button color='primary' variant='contained' onClick={send}>Send renewal email</Button>
            </DialogActions>
        </Dialog>
    </>
}

export default AddOnRenewAction
